import * as React from "react";
import { useCallback, useContext, useEffect, useState } from "react";
import { Badge } from "../../../components/ui/badge";
import { Button } from "../../../components/ui/button";
import { Stack } from "../../../components/ui/stack";
import { cn } from "../../../lib/utils";
import {
  Tabs,
  TabsContent,
  TabsList,
  TabsTrigger,
} from "../../../components/ui/tabs";
import { ZoomOut } from "lucide-react";
import { ClusterAlertBanner } from "./AlertComponent";
import { getClusterAlerts } from "./crud";
import type { ClusterAlertState } from "./types";
import { ClusterContext } from "../context";
import { TaskPrefixChart } from "./charts/TaskPrefixChart";
import { MetricsView } from "./MetricsView";
import {
  useInterval,
  useToken,
  useWorkspaceContextSlug,
} from "../../../utils/hooks";
import { ComputationListing } from "./ComputationListing";
import {
  NumberParam,
  StringParam,
  useQueryParam,
  withDefault,
} from "use-query-params";
import { LogsView } from "./LogsView";
import { ClusterTag, TimeRange } from "../types";
import { useClusterAnalyticsSummary } from "../../../crud/analytics/hooks";
import { ClusterStatistics } from "./ClusterStatistics";

import { ClusterUseCase } from "./charts/types";
import {
  CLUSTER_ALERTS_INTEVAL,
  REFRESH_LOGS_INTERVAL,
  REFRESH_METRICS_INTERVAL,
} from "./const";
import {
  BackendTypesEnum,
  TagAlertHitSchema,
  ClusterTypeEnum,
} from "../../../api-client";
import { levelToAlertSev, TagAlertsVerboseList } from "./TagAlerts";
import { UserContext } from "../../../crud/user/context";
import { AzureLogsView } from "./AzureLogsView";
import { ClusterSizeChart } from "./charts/ClusterSizeChart";
import { getDefaultTimeRange } from "./utils";
import { BatchJob } from "./BatchJob";
import { CardContent, CardHeader } from "../../../components/ui/card";

type AlertsViewProps = {
  clusterId?: number | string;
};

enum TabNames {
  Code = "Code",
  Tasks = "Tasks",
  Metrics = "Metrics",
  Logs = "Logs",
  Alerts = "Alerts",
}

const useCaseFromTag = (tag: string, n_workers?: number): ClusterUseCase => {
  if (["notebook", "run/cli", "prefect/worker"].includes(tag))
    return ClusterUseCase.singleMachine;
  if (tag === "function") return ClusterUseCase.workerOnSchedulerCluster;
  if (tag === "batch" && n_workers === 0) return ClusterUseCase.singleMachine;
  return ClusterUseCase.cluster;
};

const getTagValue = (
  label: string,
  tags: ClusterTag[] | undefined,
): string | undefined => {
  const match = (tags || []).filter((t) => t.label === label);
  return match[0]?.value;
};

export const InformationView = (props: AlertsViewProps): React.ReactElement => {
  const { clusterId, cluster } = useContext(ClusterContext);
  const [defaultMetricsTimeRange, setDefaultMetricsTimeRange] =
    useState<TimeRange>();
  const [defaultLogsTimeRange, setDefaultLogsTimeRange] = useState<TimeRange>();
  const clusterIsRunning =
    cluster &&
    !["stopped", "error", "stopping"].includes(cluster.currentState.state);
  const clusterUseCase = useCaseFromTag(
    getTagValue("coiled-cluster-type", cluster?.tagList) || "",
    cluster?.workersExceptUnprovisionable?.length,
  );
  const isGpuCluster = !!(
    getTagValue("scheduler/gpus", cluster?.tagList) ||
    getTagValue("worker/gpus", cluster?.tagList)
  );

  const [refAreaLeft, setRefAreaLeft] = React.useState<number>(0);
  const [refAreaRight, setRefAreaRight] = React.useState<number>(0);

  const [focusedComputation, setFocusedComputation] = useQueryParam(
    "computation",
    withDefault(StringParam, undefined),
  );

  useEffect(() => {
    if (!defaultMetricsTimeRange && cluster) {
      setDefaultMetricsTimeRange(getDefaultTimeRange(cluster));
    }
  }, [cluster, defaultMetricsTimeRange]);

  // Determine which tabs to show based on cluster type
  const showTaskTab = cluster?.clusterType === ClusterTypeEnum.Batch;
  const defaultTab = showTaskTab ? TabNames.Tasks : TabNames.Code;
  const tabsToShow = Object.values(TabNames).filter(
    (t) => showTaskTab || t !== TabNames.Tasks,
  );

  // TODO: Use a router here instead?
  const [tab, setTab] = useQueryParam(
    "tab",
    withDefault(StringParam, defaultTab),
  );
  const [sinceMs, setSinceMs] = useQueryParam(
    "sinceMs",
    withDefault(NumberParam, undefined),
  );
  const [untilMs, setUntilMs] = useQueryParam(
    "untilMs",
    withDefault(NumberParam, undefined),
  );

  const {
    user: { isStaff: isStaff },
  } = useContext(UserContext);

  const { data: clusterAnalyticsSummary } = useClusterAnalyticsSummary(
    cluster?.id,
  );

  const token = useToken();
  const accountSlug = useWorkspaceContextSlug();
  const [clusterAlerts, setClusterAlerts] = React.useState<ClusterAlertState>({
    loading: true,
  });

  const getOrRefreshClusterAlerts = useCallback(async () => {
    const alerts = await getClusterAlerts(accountSlug, token, clusterId);
    setClusterAlerts({ loading: false, data: alerts });
  }, [accountSlug, token, clusterId]);

  const refreshMetricsDefaultTimesRange = useCallback(async () => {
    setDefaultMetricsTimeRange((prevTimeRange) => {
      if (cluster && (!prevTimeRange || clusterIsRunning)) {
        return getDefaultTimeRange(cluster);
      }
      return prevTimeRange;
    });
  }, [setDefaultMetricsTimeRange, cluster, clusterIsRunning]);

  const refreshLogsDefaultTimesRange = useCallback(async () => {
    setDefaultLogsTimeRange((prevTimeRange) => {
      if (cluster && (!prevTimeRange || clusterIsRunning)) {
        return getDefaultTimeRange(cluster);
      }
      return prevTimeRange;
    });
  }, [setDefaultLogsTimeRange, cluster, clusterIsRunning]);

  React.useEffect(() => {
    (async () => {
      await getOrRefreshClusterAlerts();
      await refreshMetricsDefaultTimesRange();
      await refreshLogsDefaultTimesRange();
    })();
  }, [
    getOrRefreshClusterAlerts,
    refreshLogsDefaultTimesRange,
    refreshMetricsDefaultTimesRange,
  ]);

  // Keep reloading while the user is in the page
  useInterval(getOrRefreshClusterAlerts, CLUSTER_ALERTS_INTEVAL);
  useInterval(refreshMetricsDefaultTimesRange, REFRESH_METRICS_INTERVAL);
  useInterval(refreshLogsDefaultTimesRange, REFRESH_LOGS_INTERVAL);

  const [closedAlerts] = React.useState<number[]>([]);

  const closeAlert = (alertId: number) => {
    closedAlerts.push(alertId);
  };

  const resetZoom = useCallback(() => {
    setSinceMs(undefined);
    setUntilMs(undefined);
    setFocusedComputation("");
  }, [setSinceMs, setUntilMs, setFocusedComputation]);

  const setTimeRange = useCallback(
    (range?: TimeRange) => {
      if (range) {
        setSinceMs(range[0] as number);
        setUntilMs(range[1] as number);
      } else {
        resetZoom();
      }
    },
    [setSinceMs, setUntilMs, resetZoom],
  );

  const timeRange: TimeRange | undefined =
    sinceMs && untilMs ? [sinceMs, untilMs] : defaultMetricsTimeRange;

  const alertsToShow = (cluster?.alertList || []).filter(
    (alert: TagAlertHitSchema) => isStaff || !alert.staffOnly,
  );

  const isBatch = cluster?.clusterType === ClusterTypeEnum.Batch;
  return (
    <Stack spacing={0} className="flex flex-col">
      <CardContent className="px-7 pt-4 pb-0">
        <Stack>
          {clusterAlerts.data
            ?.filter((alert) => !closedAlerts.includes(alert.id))
            .map((alert, index) => (
              <div key={index} className="mb-2">
                <ClusterAlertBanner
                  id={alert.id}
                  level={alert.level}
                  title={alert.title}
                  originalMessage={alert.originalMessage}
                  coiledMessage={alert.coiledMessage}
                  recommendation={alert.recommendation}
                  clusterId={alert.clusterId}
                  label={alert.label}
                  errorType={alert.errorType}
                  closeAlert={closeAlert}
                />
              </div>
            ))}
          {sinceMs && untilMs ? <ZoomOutHeader onZoomOut={resetZoom} /> : null}

          <ClusterStatistics
            cluster={cluster}
            analyticsSummary={clusterAnalyticsSummary}
            clusterIsRunning={clusterIsRunning}
          />

          {isBatch ? (
            <ClusterSizeChart clusterId={clusterId} />
          ) : (
            <TaskPrefixChart
              clusterId={clusterId}
              timeRange={timeRange}
              defaultTimeRange={defaultMetricsTimeRange}
              setTimeRange={setTimeRange}
              refAreaLeft={refAreaLeft}
              refAreaRight={refAreaRight}
              setRefAreaLeft={setRefAreaLeft}
              setRefAreaRight={setRefAreaRight}
              clusterIsRunning={clusterIsRunning}
            />
          )}
        </Stack>
      </CardContent>

      <Tabs value={tab} onValueChange={setTab} className="w-full mt-4">
        <CardHeader className="border-b-1 border-gray-200">
          <TabsList>
            {tabsToShow.map((key) => (
              <TabsTrigger
                key={key}
                value={key}
                disabled={key === TabNames.Alerts && alertsToShow.length === 0}
                className="flex items-center gap-1"
              >
                {key === TabNames.Alerts ? (
                  <>
                    <span>{key}</span>
                    <Badge
                      variant="secondary"
                      className={cn(
                        "ml-0 mb-3 text-xs rounded-full min-w-[20px] h-5 px-1 font-semibold",
                        levelToAlertSev(alertsToShow[0]?.level) ===
                          "destructive" && "bg-red-500 text-white shadow-xs",
                        levelToAlertSev(alertsToShow[0]?.level) === "default" &&
                          "bg-orange-600 text-white shadow-xs",
                        levelToAlertSev(alertsToShow[0]?.level) === "info" &&
                          "bg-info text-gray-800 hidden",
                      )}
                    >
                      {alertsToShow.length}
                    </Badge>
                  </>
                ) : (
                  key
                )}
              </TabsTrigger>
            ))}
          </TabsList>
        </CardHeader>
        <CardContent className="px-8 py-4">
          <TabsContent value={TabNames.Code}>
            <ComputationListing
              clusterId={cluster?.id}
              focusedComputation={focusedComputation}
              setFocusedComputation={setFocusedComputation}
              timeRange={timeRange}
              defaultTimeRange={defaultMetricsTimeRange}
              setTimeRange={setTimeRange}
              setRefAreaLeft={setRefAreaLeft}
              setRefAreaRight={setRefAreaRight}
              zoomOut={resetZoom}
            />
          </TabsContent>

          <TabsContent value={TabNames.Tasks}>
            <BatchJob clusterId={cluster?.id} />
          </TabsContent>

          <TabsContent value={TabNames.Metrics}>
            <MetricsView
              clusterId={clusterId}
              timeRange={timeRange}
              setTimeRange={setTimeRange}
              defaultTimeRange={defaultMetricsTimeRange}
              refAreaLeft={refAreaLeft}
              refAreaRight={refAreaRight}
              setRefAreaLeft={setRefAreaLeft}
              setRefAreaRight={setRefAreaRight}
              clusterIsRunning={clusterIsRunning}
              clusterUseCase={clusterUseCase}
              hasGpu={isGpuCluster}
            />
          </TabsContent>

          <TabsContent value={TabNames.Logs}>
            {cluster?.backendType === BackendTypesEnum.VmAzure ? (
              <AzureLogsView
                clusterId={clusterId}
                cluster={cluster}
                timeRange={timeRange}
                setTimeRange={setTimeRange}
                defaultLogTimeRange={defaultLogsTimeRange}
                defaultMetricsTimeRange={defaultMetricsTimeRange}
                refAreaLeft={refAreaLeft}
                refAreaRight={refAreaRight}
                setRefAreaLeft={setRefAreaLeft}
                setRefAreaRight={setRefAreaRight}
                clusterIsRunning={clusterIsRunning}
                zoomOut={resetZoom}
              />
            ) : (
              <LogsView
                clusterId={clusterId}
                cluster={cluster}
                timeRange={timeRange}
                setTimeRange={setTimeRange}
                defaultLogTimeRange={defaultLogsTimeRange}
                defaultMetricsTimeRange={defaultMetricsTimeRange}
                refAreaLeft={refAreaLeft}
                refAreaRight={refAreaRight}
                setRefAreaLeft={setRefAreaLeft}
                setRefAreaRight={setRefAreaRight}
                clusterIsRunning={clusterIsRunning}
                zoomOut={resetZoom}
              />
            )}
          </TabsContent>

          <TabsContent value={TabNames.Alerts}>
            {!!alertsToShow && (
              <TagAlertsVerboseList alertList={alertsToShow} />
            )}
          </TabsContent>
        </CardContent>
      </Tabs>
    </Stack>
  );
};

const ZoomOutHeader = ({
  onZoomOut,
}: {
  onZoomOut: () => void;
}): React.ReactElement => (
  <div className="sticky top-[-24px] right-0 h-0 w-full z-[2] text-right">
    <Button
      variant="outline"
      size="sm"
      onClick={onZoomOut}
      className="mt-[78px] mr-[22px] bg-background hover:bg-muted"
    >
      <ZoomOut className="mr-1 h-4 w-4" />
      Zoom Out
    </Button>
  </div>
);
